import { useCallback, useEffect, useState } from 'react';
import {
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { useSelector } from 'react-redux';
import { db } from '@/shared/lib';
import { Store, dispatch } from '@/redux';
import { LoggerService } from '@/shared/logger';
import { useHttpMutation } from '@/api';
import { API_URLS } from '../../consts';
import { updateUserCoins } from '@/redux/reducers';

/**
 * Per-direction DM quota stored in Firestore.
 *
 *   chat_quotas / {senderUID}_{receiverUID}
 *     {
 *       senderUID:    string,
 *       receiverUID:  string,
 *       remaining:    number,    // messages left in this paid window
 *       limit:        number,    // window size (currently CHAT_MESSAGES_PER_FEE = 100)
 *       startedAt:    Timestamp,
 *     }
 *
 * One fee (CHAT_ENTRY_FEE = 100 coins, server-enforced) tops the
 * `remaining` counter back up to `limit`. The decrement happens
 * client-side immediately after every successful chat write. Firestore
 * security rules also gate `chat` create on the presence of the quota
 * doc with `remaining > 0`.
 */
export const CHAT_QUOTAS_COLLECTION = 'chat_quotas';
export const CHAT_ENTRY_FEE = 100;
export const CHAT_MESSAGES_PER_FEE = 100;

export const chatQuotaDocID = (senderUID: string, receiverUID: string) =>
  `${senderUID}_${receiverUID}`;

export interface ChatQuotaRecord {
  senderUID: string;
  receiverUID: string;
  remaining: number;
  limit: number;
  startedAt?: Timestamp;
}

/** Live read of the quota the current user has for messaging `otherUID`. */
export const useChatQuota = (otherUID?: string | null) => {
  const { firebaseUID } = useSelector((s: Store) => s.userReducer);
  const [quota, setQuota] = useState<ChatQuotaRecord | null>(null);
  const [loading, setLoading] = useState<boolean>(
    !!firebaseUID && !!otherUID
  );

  useEffect(() => {
    if (!firebaseUID || !otherUID) {
      setQuota(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    const unsub = onSnapshot(
      doc(db, CHAT_QUOTAS_COLLECTION, chatQuotaDocID(firebaseUID, otherUID)),
      snap => {
        if (snap.exists()) {
          setQuota(snap.data() as ChatQuotaRecord);
        } else {
          setQuota(null);
        }
        setLoading(false);
      },
      err => {
        LoggerService.error('chat quota listener failed', err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [firebaseUID, otherUID]);

  const remaining = quota?.remaining ?? 0;
  const hasQuota = remaining > 0;

  return { quota, remaining, hasQuota, loading };
};

/** Decrement the caller's quota toward `otherUID` by 1. Safe no-op when not set up. */
export const decrementChatQuota = async (
  senderUID: string,
  receiverUID: string
) => {
  if (!senderUID || !receiverUID) return;
  try {
    await updateDoc(
      doc(db, CHAT_QUOTAS_COLLECTION, chatQuotaDocID(senderUID, receiverUID)),
      { remaining: increment(-1) }
    );
  } catch (err) {
    // Don't break the chat write if the decrement fails; the rule guard
    // is the source of truth and the listener will reconcile.
    LoggerService.warn?.('decrementChatQuota failed', err);
  }
};

/**
 * Reset / create the quota doc to a fresh `limit` after a successful
 * payment.
 *
 * Implemented as delete-then-create instead of overwrite because the
 * security rule's `update` predicate intentionally only allows
 * decrementing `remaining` by 1 (otherwise a client could mint
 * unlimited free messages by writing remaining=100 directly). Delete
 * is permitted for either party, and create requires remaining=100,
 * which is exactly what we want for a fresh top-up.
 *
 * If no doc exists yet, the delete call no-ops (Firestore returns
 * 200 for delete-non-existent) and we just create.
 */
export const createChatQuota = async (
  senderUID: string,
  receiverUID: string
) => {
  if (!senderUID || !receiverUID) return;
  const ref = doc(db, CHAT_QUOTAS_COLLECTION, chatQuotaDocID(senderUID, receiverUID));
  // Best-effort delete — ignore errors for the (common) case where the
  // doc doesn't exist yet.
  try {
    await deleteDoc(ref);
  } catch (err) {
    LoggerService.warn?.('createChatQuota delete-before-create failed', err);
  }
  await setDoc(ref, {
    senderUID,
    receiverUID,
    remaining: CHAT_MESSAGES_PER_FEE,
    limit: CHAT_MESSAGES_PER_FEE,
    startedAt: serverTimestamp(),
  });
};

/** Delete the quota doc (e.g. on thread delete). */
export const deleteChatQuota = async (
  senderUID: string,
  receiverUID: string
) => {
  if (!senderUID || !receiverUID) return;
  await deleteDoc(
    doc(db, CHAT_QUOTAS_COLLECTION, chatQuotaDocID(senderUID, receiverUID))
  );
};

/**
 * Mutation hook that pays the chat entry fee on the server and creates a
 * fresh Firestore quota doc on success. Returns `{ purchase, isLoading,
 * error }`. Updates the local redux coin balance with the server's
 * authoritative new balance.
 */
export const usePurchaseChatQuota = (otherUID?: string | null) => {
  const { firebaseUID } = useSelector((s: Store) => s.userReducer);

  const { mutateAsync, isLoading, error } = useHttpMutation<
    { balance: number },
    { receiverUID: string }
  >(API_URLS.SPEND_COINS_FOR_CHAT, 'POST');

  const purchase = useCallback(async () => {
    if (!firebaseUID || !otherUID) return null;
    const res = await mutateAsync({ receiverUID: otherUID });
    // Server-confirmed new balance — push to redux so the top-bar coin
    // counter updates without a refetch.
    if (typeof res?.balance === 'number') {
      dispatch(updateUserCoins(res.balance));
    }
    await createChatQuota(firebaseUID, otherUID);
    return res;
  }, [firebaseUID, otherUID, mutateAsync]);

  return { purchase, isLoading, error };
};


// ─────────────────────────────────────────────────────────────────────────────
// Group-chat quotas (per-room, per-user)
// ─────────────────────────────────────────────────────────────────────────────
//
// Layout mirrors the DM quota, with a different collection so the rules
// can encode a different doc-id pattern without overloading /chat_quotas.
//
//   group_chat_quotas / {roomID}_{userUID}
//     {
//       userUID:    string,
//       roomID:     string,
//       remaining:  number,
//       limit:      number,
//       startedAt:  Timestamp,
//     }
//
// One fee tops `remaining` back up to `limit` (100). The client decrements
// after every successful group-chat write; Firestore rules enforce the
// per-doc invariant of the 100-message window.

export const GROUP_CHAT_QUOTAS_COLLECTION = 'group_chat_quotas';

export const groupChatQuotaDocID = (roomID: string, userUID: string) =>
  `${roomID}_${userUID}`;

export interface GroupChatQuotaRecord {
  userUID: string;
  roomID: string;
  remaining: number;
  limit: number;
  startedAt?: Timestamp;
}

/** Live read of the caller's group-chat quota in `roomID`. */
export const useGroupChatQuota = (roomID?: string | null) => {
  const { firebaseUID } = useSelector((s: Store) => s.userReducer);
  const [quota, setQuota] = useState<GroupChatQuotaRecord | null>(null);
  const [loading, setLoading] = useState<boolean>(!!firebaseUID && !!roomID);

  useEffect(() => {
    if (!firebaseUID || !roomID) {
      setQuota(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(
        db,
        GROUP_CHAT_QUOTAS_COLLECTION,
        groupChatQuotaDocID(roomID, firebaseUID)
      ),
      snap => {
        if (snap.exists()) setQuota(snap.data() as GroupChatQuotaRecord);
        else setQuota(null);
        setLoading(false);
      },
      err => {
        LoggerService.error('group chat quota listener failed', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [firebaseUID, roomID]);

  const remaining = quota?.remaining ?? 0;
  const hasQuota = remaining > 0;
  return { quota, remaining, hasQuota, loading };
};

/** Decrement the caller's group-chat quota in `roomID` by 1. */
export const decrementGroupChatQuota = async (
  roomID: string,
  userUID: string
) => {
  if (!roomID || !userUID) return;
  try {
    await updateDoc(
      doc(
        db,
        GROUP_CHAT_QUOTAS_COLLECTION,
        groupChatQuotaDocID(roomID, userUID)
      ),
      { remaining: increment(-1) }
    );
  } catch (err) {
    LoggerService.warn?.('decrementGroupChatQuota failed', err);
  }
};

/** Reset / create the group-chat quota doc. delete-then-create so the
 *  update rule's strict "decrement by 1" predicate doesn't reject the
 *  renewal. Mirrors createChatQuota in the DM helper. */
export const createGroupChatQuota = async (
  roomID: string,
  userUID: string
) => {
  if (!roomID || !userUID) return;
  const ref = doc(
    db,
    GROUP_CHAT_QUOTAS_COLLECTION,
    groupChatQuotaDocID(roomID, userUID)
  );
  try {
    await deleteDoc(ref);
  } catch (err) {
    LoggerService.warn?.('createGroupChatQuota delete-before-create failed', err);
  }
  await setDoc(ref, {
    userUID,
    roomID,
    remaining: CHAT_MESSAGES_PER_FEE,
    limit: CHAT_MESSAGES_PER_FEE,
    startedAt: serverTimestamp(),
  });
};

/** Mutation hook that pays the group-chat entry fee on the server and
 *  creates a fresh Firestore quota doc on success. */
export const usePurchaseGroupChatQuota = (roomID?: string | null) => {
  const { firebaseUID } = useSelector((s: Store) => s.userReducer);
  const { mutateAsync, isLoading, error } = useHttpMutation<
    { balance: number },
    { roomID: string }
  >(API_URLS.SPEND_COINS_FOR_CHAT, 'POST');

  const purchase = useCallback(async () => {
    if (!firebaseUID || !roomID) return null;
    const res = await mutateAsync({ roomID });
    if (typeof res?.balance === 'number') {
      dispatch(updateUserCoins(res.balance));
    }
    await createGroupChatQuota(roomID, firebaseUID);
    return res;
  }, [firebaseUID, roomID, mutateAsync]);

  return { purchase, isLoading, error };
};

